import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import MinimizableDialog from 'app/shared-components/layout/MinimizableDialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import * as yup from 'yup';
import _ from '@lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { forwardRef, useState, useEffect, useRef} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useThemeMediaQuery } from '@fuse/hooks';
import DefaultButton from '../layout/DefaultButton';
import ControlledTextField from 'app/shared-components/form/ControlledTextField';
import ControlledWYSIWYG from 'app/shared-components/form/ControlledWYSIWYG';
import ControlledSwitch from 'app/shared-components/form/ControlledSwitch';
import { selectArticleDialogId, selectArticleById, setArticleDialogId, updateArticle, createArticle, deleteArticle } from 'app/store/articlesSlice';
import ReactTags from 'app/shared-components/tags/ReactTags';
import isValidTagName from 'app/shared-components/tags/isValidTagName';
import { SEPARATORS } from 'app/shared-components/tags/constants';
import { warningMessage } from 'app/shared-components/util/message';
import 'app/shared-components/tags/tags.css';
import Avatar from '@mui/material/Avatar';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import CropImageDialog from 'app/shared-components/CropImageDialog';
import uploadFile from 'app/shared-components/uploadFunction';
import { startLoading, stopLoading } from 'app/store/utilSlice';
import { showMessage } from 'app/store/fuse/messageSlice';
import getFileURL from 'app/shared-components/util/getFileURL';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import confirmationDialog from '../util/confirmationDialog';
import { apiGet } from 'app/shared-components/util/restAPI';
import dispatchSubmit from 'app/shared-components/util/dispatchSubmit';
const isValidSlug = (slug) => {
  if (!slug) return true; // Optional field
  if (slug.length < 3 || slug.length > 50) return false;
  if (slug.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/)) return false;
  return slug.match(/^[a-z0-9-]+$/);
};

const generateSlugFromTitle = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .substring(0, 50);
};

const schema = yup.object().shape({
  title: yup.string().required('Title is required').max(100, 'Title must be 100 characters or less'),
  summary: yup.string().required('Summary is required').max(300, 'Summary must be 300 characters or less'),
  content: yup.string()
    .test('content-validation', 'Content is required', function(value) {
      if (!value) return this.createError({ message: 'Content is required' });
      const textContent = value.replace(/<[^>]*>/g, '').trim();
      if (textContent.length === 0) return this.createError({ message: 'Content is required' });
      if (textContent.length > 10000) return this.createError({ message: 'Content must be 10,000 characters or less' });
      return true;
    }),
  slug: yup.string().test('slug-validation', 'Invalid slug format. Must be 3-50 characters, lowercase letters, numbers, and hyphens only, cannot be a UUID.', isValidSlug),
  tags: yup.array().of(yup.string()),
  status: yup.string().oneOf(['Draft', 'Published']),
});

function EditArticleDialog({}) {
  const dispatch = useDispatch();
  const fullScreen = useThemeMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [openHeaderDialog, setOpenHeaderDialog] = useState(false);
  const [selectedHeaderFile, setSelectedHeaderFile] = useState();
  const headerFileInput = useRef();
  const [tags, setTags] = useState([]);
  const articleId = useSelector(selectArticleDialogId);
  const isNew = articleId === 'new';
  const article = useSelector(selectArticleById(isNew ? null : articleId));
  const [actualArticleId, setActualArticleId] = useState(isNew ? null : articleId);
  const [headerImageUrl, setHeaderImageUrl] = useState('');
  const [headerImageId, setHeaderImageId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isInitialArticle, setIsInitialArticle] = useState(false);
  const [headerExpanded, setHeaderExpanded] = useState(false);
  const [userEditedSlug, setUserEditedSlug] = useState(false);
  
  const defaultValues = {
    title: article?.title || (isNew ? 'Draft' : ''),
    summary: article?.summary || (isNew ? 'Draft' : ''),
    content: article?.content || (isNew ? 'Draft' : ''),
    slug: article?.slug || '',
    tags: article?.tags || [],
    status: article?.status || 'Draft',
  };
  
  const { handleSubmit, formState, control, reset, watch, setValue } = useForm({
    mode: 'onChange',
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
  });

  const watchedTitle = watch('title');
  const watchedSlug = watch('slug');

  // Auto-populate slug from title
  useEffect(() => {
    if (!userEditedSlug && watchedTitle && watchedTitle !== 'Draft') {
      const generatedSlug = generateSlugFromTitle(watchedTitle);
      if (generatedSlug !== watchedSlug) {
        setValue('slug', generatedSlug);
      }
    }
  }, [watchedTitle, userEditedSlug, setValue, watchedSlug]);

  const { isValid, dirtyFields, errors } = formState;
  
  // Reset state when opening new article dialog
  useEffect(() => {
    if (isNew) {
      setTags([]);
      setHeaderImageUrl('');
      setHeaderImageId(null);
      setHasChanges(false);
      setIsInitialArticle(false);
      setHeaderExpanded(false);
      setUserEditedSlug(false);
      setActualArticleId(null);
      
      // Reset form to new article defaults
      reset({
        title: '',
        summary: '',
        content: '',
        slug: '',
        tags: [],
        status: 'Draft',
      });
    }
  }, [isNew, articleId, reset]);

  useEffect(() => {
    if (isNew && !actualArticleId) {
      dispatch(createArticle({ title: 'Draft', summary: 'Draft', content: 'Draft', tags: [], status: 'Init' }))
        .unwrap()
        .then((newArticle) => {
          setActualArticleId(newArticle.id);
          setIsInitialArticle(true);
        });
    }
  }, [isNew, actualArticleId, dispatch]);

  useEffect(() => {
    if (article && !isNew) {
      const articleTags = article?.tags || [];
      const tagObjects = articleTags.map(tag => ({ id: tag, className: '' }));
      setTags(tagObjects);
      setHeaderImageUrl(article?.headerImageUrl || '');
      setHeaderImageId(article?.headerImageId || null);
      setActualArticleId(article.id);
      reset({
        title: article?.title || '',
        summary: article?.summary || '',
        content: article?.content || '',
        slug: article?.slug || '',
        tags: articleTags,
        status: article?.status || 'Draft',
      });
      setUserEditedSlug(Boolean(article?.slug));
    }
  }, [article, reset, articleId, isNew]);
  

  const onSelectHeaderFile = (e) => {
    e.preventDefault();
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    setSelectedHeaderFile(files[0]);
    setOpenHeaderDialog(true);
  };
  
  function handleCloseHeaderDialog() {
    setOpenHeaderDialog(false);
  }
  
  function saveHeaderImage(dataUrl, file){
    dispatch(startLoading());
    setOpenHeaderDialog(false);
    
    uploadFile(file, actualArticleId, 'article_header').then((fileId) => {
      getFileURL(fileId).then((url) => {
        setHeaderImageUrl(url.url);
        setHeaderImageId(fileId);
        setHasChanges(true);
        dispatch(stopLoading());
      });
    })
    .catch((err) => {
      dispatch(stopLoading());
      dispatch(showMessage({
        message: err.message,
        autoHideDuration: 6000,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        },
        variant: 'error'
      }));
    });
  }

  if (!articleId) {
    return null;
  }

  async function checkSlugAvailability(slug) {
    if (!slug) return true;
    try {
      const data = await apiGet('CoreAPI3', `/x3/article/checkslug?slug=${encodeURIComponent(slug)}`, {});
      return data.available;
    } catch (error) {
      console.error('Error checking slug availability:', error);
      return false;
    }
  }

  async function onSubmit(data) {
    // Check slug availability if slug is provided and changed
    if (data.slug && (isNew || data.slug !== article?.slug)) {
      const isAvailable = await checkSlugAvailability(data.slug);
      if (!isAvailable) {
        dispatch(showMessage({
          message: 'Slug already exists. Please choose a different slug.',
          autoHideDuration: 6000,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          },
          variant: 'error'
        }));
        return;
      }
    }

    if (data.status === 'Draft') {
      confirmationDialog(dispatch, {
        title: "Save as Draft?",
        content: "This article is in Draft status - it will not be visible to others on your timeline or public profile. To publish the article, simply update the status to Published and save.",
        onOk: () => submitArticle(data),
        onCancel: () => {},
        okText: "Save Draft",
        cancelText: "Cancel",
      });
    } else {
      submitArticle(data);
    }
  }

  function submitArticle(data) {
    const tagStrings = tags.map(tag => tag.id);
    const submitData = { 
      ...data, 
      tags: tagStrings, 
      headerImageId: headerImageId ? headerImageId : undefined,
      featured: article?.featured // Preserve original featured status
    };
    const idToUse = actualArticleId || articleId;
    
    dispatchSubmit({
      dispatch,
      submitFunction: updateArticle({ id: idToUse, ...submitData }),
      onSuccess: onClose,
      successMessage: 'Article saved successfully!'
    });
  }

  const handleDeleteTag = (index) => {
    setTags(tags.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleAddTag = (tag) => {
    if (isValidTagName(tag.id)) {
      setTags([...tags, tag]);
      setHasChanges(true);
    } else {
      warningMessage(dispatch, tag.id + " is not valid. Tags must have only lower case letters, numbers, or underscore, and must start with a letter.");
    }
  };

  function resetState() {
    setTags([]);
    setActualArticleId(isNew ? null : articleId);
    setHeaderImageUrl('');
    setHeaderImageId(null);
    setHasChanges(false);
    setIsInitialArticle(false);
    setHeaderExpanded(false);
    setOpenHeaderDialog(false);
    setSelectedHeaderFile(null);
    setUserEditedSlug(false);
    reset();
  }

  function onCancel() {
    if (isNew && actualArticleId && isInitialArticle) {
      dispatch(deleteArticle({ articleId: actualArticleId }));
    }
    resetState();
    dispatch(setArticleDialogId(null));
  }

  function onClose() {
    resetState();
    dispatch(setArticleDialogId(null));
  }

  return (
    <MinimizableDialog
      open={Boolean(articleId)}
      title={isNew ? "Create Article" : "Edit Article"}
      handleClose={onClose}
      showClose={true}
      fullScreen={fullScreen}
      width="800px"
      maxWidth="1000px"
      height="95%"
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full w-full">
        {/* Collapsible Header Image Section */}
        <div className="border-b">
          <div 
            className="flex items-center justify-between p-12 cursor-pointer hover:bg-gray-50"
            onClick={() => setHeaderExpanded(!headerExpanded)}
          >
            <Typography variant="subtitle2">Header Image</Typography>
            <FuseSvgIcon size={16}>
              {headerExpanded ? 'heroicons-outline:chevron-up' : 'heroicons-outline:chevron-down'}
            </FuseSvgIcon>
          </div>
          {headerExpanded && (
            <div className="relative">
              <img
                className="w-full bg-gray-200"
                src={headerImageUrl || '/assets/images/article/default_article_header.jpg'}
                alt="Article Header"
                style={{ aspectRatio: '4/3', objectFit: 'cover' }}
              />
              <div className="opacity-0 hover:opacity-100 absolute top-0 z-10 w-full h-full">
                <div className="absolute top-2 right-2 z-10">
                  <Avatar className="bg-stone-300/50 hover:bg-stone-400">
                    <FuseSvgIcon onClick={() => headerFileInput.current.click()} size={20}>heroicons-outline:pencil</FuseSvgIcon>
                    <input type="file" ref={headerFileInput} accept="image/*" hidden onChange={onSelectHeaderFile} onClick={(event) => event.target.value= ''}/>
                  </Avatar>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <DialogContent classes={{ root: 'px-16 flex-1 flex flex-col' }}>
          <div className="flex flex-col h-full">
            <div className="flex gap-16">
              <ControlledTextField 
                name="title" 
                label="Title" 
                width="100%"
                control={control} 
                errors={errors}
                className={'mt-0 mb-0'}
              />
              {!fullScreen && (
                <Controller
                  name="slug"
                  control={control}
                  render={({ field, fieldState }) => (
                    <ControlledTextField 
                      name="slug" 
                      label="URL Slug (optional)" 
                      control={control} 
                      errors={errors}
                      className="mt-0"
                      style={{ width: '300px', minWidth: '300px' }}
                      onChange={(e) => {
                        setUserEditedSlug(true);
                        field.onChange(e.target.value);
                      }}
                    />
                  )}
                />
              )}
              <ControlledSwitch
                name="status"
                control={control}
                trueValue="Published"
                falseValue="Draft"
                label={(value) => value === 'Published' ? 'Published' : 'Draft'}
                className="whitespace-nowrap"
                style={{ width: '120px', minWidth: '120px' }}
              />
            </div>
            
            {fullScreen && (
              <Controller
                name="slug"
                control={control}
                render={({ field, fieldState }) => (
                  <ControlledTextField 
                    name="slug" 
                    label="URL Slug (optional)" 
                    width="100%"
                    control={control} 
                    errors={errors}
                    className="mt-16"
                    onChange={(e) => {
                      setUserEditedSlug(true);
                      field.onChange(e.target.value);
                    }}
                  />
                )}
              />
            )}
            
            <ControlledTextField 
              name="summary" 
              label="Summary" 
              width="100%"
              multiline
              rows={3}
              control={control} 
              errors={errors}
              className="mt-16"
            />
            
            <div className="flex-1 mt-16" style={{ minHeight: '300px', display: 'flex', flexDirection: 'column' }}>
              <ControlledWYSIWYG 
                key={actualArticleId || 'no-id'}
                name="content"
                //articleId={actualArticleId}
                initialContent={article?.content || ''} 
                control={control} 
                errors={errors}
                className="flex-1"
                style={{ height: '100%' }}
              />
            </div>
            
            <div className="mt-16" style={{ 
              display: 'flex', 
              alignItems: 'center',
            }}>
              <style>{`
                .ReactTags__tagInput { margin-top: 0 !important; }
                .ReactTags__tagInputField { height: 40px !important; padding: 10px 12px !important; box-sizing: border-box !important; }
              `}</style>
              <ReactTags
                tags={tags}
                labelField="id"
                suggestions={[]}
                separators={[SEPARATORS.ENTER, SEPARATORS.COMMA]}
                handleDelete={handleDeleteTag}
                handleAddition={handleAddTag}
                inputFieldPosition="inline"
                placeholder="Add Tag"
                editable
                clearAll={false}
                maxTags={10}
              />
            </div>
          </div>
        </DialogContent>

        <DialogActions className="flex justify-end py-16 sm:py-24 px-24">
          <div className="flex items-center space-x-8">
            <DefaultButton text="Cancel" onClick={onCancel} />
            <DefaultButton 
              text="Save"
              disabled={(_.isEmpty(dirtyFields) && !hasChanges) || !isValid}
              submit 
            />
          </div>
        </DialogActions>
      </form>
      
      <CropImageDialog 
        aspectRatio={1.5}
        resize={{width:1200, height:800}}
        dialogTitle="Crop Header Image" 
        file={selectedHeaderFile} 
        open={openHeaderDialog}
        onSaveCrop={saveHeaderImage} 
        onCancel={handleCloseHeaderDialog}
      />

    </MinimizableDialog>
  );
}

export default EditArticleDialog;